import { useState, useCallback } from 'react'
import { Modal, btnPrimary, btnSecondary, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'

type Scope = 'all_orgs' | 'org_scoped' | 'view_scoped'

const VIEW_OPTIONS = ['results', 'orders', 'patients'] as const

export interface ExternalApiKeyRow {
  readonly id: string
  readonly name: string
  readonly scope: Scope
  readonly orgHmac: string | null
  readonly allowedViews: readonly string[] | null
  readonly rateLimitPerMinute: number
  readonly createdAt: string
  readonly updatedAt: string
  readonly revokedAt: string | null
  readonly lastUsedAt: string | null
}

function Field({
  label,
  required,
  children,
  hint,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  hint?: string
}) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '4px' }}>{hint}</div>}
    </div>
  )
}

// Shown once, immediately after create. Key cannot be retrieved again.
export function PlaintextRevealModal({
  plaintext,
  keyName,
  onClose,
}: {
  readonly plaintext: string
  readonly keyName: string
  readonly onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(plaintext)
    setCopied(true)
  }, [plaintext])

  return (
    <Modal isOpen={true} onClose={onClose} title={`API Key Created — ${keyName}`} width="620px">
      <div style={{ padding: '1.25rem' }}>
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            padding: '0.75rem 1rem',
            marginBottom: '1.25rem',
            fontSize: '0.875rem',
            color: '#92400e',
          }}
        >
          Copy the key value now. It will NOT be shown again after closing this dialog.
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '6px',
            }}
          >
            Key Value
          </div>
          <div
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              padding: '0.75rem',
              borderRadius: '6px',
              wordBreak: 'break-all',
              marginBottom: '0.5rem',
            }}
            data-testid="external-api-key-plaintext"
          >
            {plaintext}
          </div>
          <button style={{ ...btnPrimary, fontSize: '0.8125rem' }} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>
        <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '1rem' }}>
          Use this value in the <code>X-API-Key</code> header when calling
          <code> /api/external/*</code>.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={btnSecondary} onClick={onClose}>
            I have copied the key — Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export function ExternalApiKeyModal({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void
  readonly onCreated: (plaintext: string, name: string) => void
}) {
  const [name, setName] = useState('')
  const [scope, setScope] = useState<Scope>('all_orgs')
  const [orgHmac, setOrgHmac] = useState('')
  const [allowedViews, setAllowedViews] = useState<string[]>(['results'])
  const [rateLimit, setRateLimit] = useState(60)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleView = useCallback((v: string) => {
    setAllowedViews((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return setError('Name is required')
    if (scope === 'org_scoped' && !orgHmac.trim()) {
      return setError('orgHmac is required for org_scoped keys')
    }
    if (scope === 'view_scoped' && allowedViews.length === 0) {
      return setError('Select at least one allowed view')
    }
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        scope,
        rateLimitPerMinute: rateLimit,
      }
      if (scope === 'org_scoped') body.orgHmac = orgHmac.trim()
      if (scope === 'view_scoped') body.allowedViews = allowedViews
      const res = await api.post<{ plaintext: string; row: ExternalApiKeyRow }>('/admin/external-api-keys', body)
      const plaintext = res.data?.plaintext
      if (!plaintext) throw new Error('No plaintext in response')
      onCreated(plaintext, name.trim())
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create key')
      setSaving(false)
    }
  }, [name, scope, orgHmac, allowedViews, rateLimit, onCreated])

  return (
    <Modal isOpen={true} onClose={onClose} title="Create External API Key" width="560px">
      <div style={{ padding: '1.25rem' }}>
        {error && (
          <div
            style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              padding: '0.625rem 0.875rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              color: '#991b1b',
            }}
          >
            {error}
          </div>
        )}

        <Field label="Name" required>
          <input
            style={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Billing consumer — Acme Corp"
          />
        </Field>

        <Field label="Scope" required hint="Controls what analytics rows the key can read">
          <select style={input} value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
            <option value="all_orgs">all_orgs — full cross-org access</option>
            <option value="org_scoped">org_scoped — pinned to one customer org</option>
            <option value="view_scoped">view_scoped — limited to selected views</option>
          </select>
        </Field>

        {scope === 'org_scoped' && (
          <Field
            label="Organization HMAC"
            required
            hint="SHA-256 HMAC of the customer's organizationId (matches the analytics projector output)"
          >
            <input
              style={input}
              value={orgHmac}
              onChange={(e) => setOrgHmac(e.target.value)}
              placeholder="hex digest"
            />
          </Field>
        )}

        {scope === 'view_scoped' && (
          <Field label="Allowed Views" required>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {VIEW_OPTIONS.map((v) => (
                <label
                  key={v}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  <input type="checkbox" checked={allowedViews.includes(v)} onChange={() => toggleView(v)} />
                  {v}
                </label>
              ))}
            </div>
          </Field>
        )}

        <Field label="Rate Limit (per minute)">
          <input
            style={input}
            type="number"
            min={1}
            max={10_000}
            value={rateLimit}
            onChange={(e) => setRateLimit(Number(e.target.value))}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1rem' }}>
          <button style={btnSecondary} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button style={btnPrimary} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating…' : 'Create Key'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
